import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  IUser,
} from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IComparisonPopulationSetting } from '../comparisonPopulationSettingsProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

/**
 * @description the complete document in the database
 */
export interface IQuadrantReportSettingEntity
  extends IQuadrantReportSettingOptions {
  selected: ISettingsTemplate<IQuadrantReportSettings>;
}

export interface IQuadrantReportSettings {
  xAxis: {
    testType: ISelectableTestType;
    tags: ISelectableTag[];
    metric: ISelectableMetric;
  };
  yAxis: {
    testType: ISelectableTestType;
    tags: ISelectableTag[];
    metric: ISelectableMetric;
  };
}

/**
 * @description the subset of settings that are not template specific
 */
interface IQuadrantReportSettingOptions {
  timeframe: IDateRange;
  comparisonPopulation: IComparisonPopulationSetting;
}

export interface IQuadrantReportSettingsState
  extends IQuadrantReportSettingOptions {
  selected: ISettingsTemplate<IQuadrantReportSettings>;
  templates: Array<ISettingsTemplate<IQuadrantReportSettings>>;
}

/**
 * @description observes the quadrant report settings
 */
export const quadrantReportSettingsProvider =
  (): Observable<IQuadrantReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap(
        (
          user: IUser
        ): Observable<
          [
            ISettingsTemplate<IQuadrantReportSettings>,
            IQuadrantReportSettingOptions,
            Array<ISettingsTemplate<IQuadrantReportSettings>>
          ]
        > => {
          const listenerArgs = {
            userId: user.id,
            firestore: user.services.firestore,
          };
          return combineLatest([
            selectedTemplateProvider(listenerArgs),
            quadrantSettingsOptionsProvider(listenerArgs),
            templatesProvider(listenerArgs),
          ]);
        }
      ),
      map(
        (
          result: [
            ISettingsTemplate<IQuadrantReportSettings>,
            IQuadrantReportSettingOptions,
            Array<ISettingsTemplate<IQuadrantReportSettings>>
          ]
        ): IQuadrantReportSettingsState => {
          return {
            selected: result[0],
            templates: result[2],
            ...result[1],
          };
        }
      )
    );
  };

interface IListenerArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: IListenerArgs
): Observable<Array<ISettingsTemplate<IQuadrantReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/quadrantReport/templates`
      ) as CollectionReference<ISettingsTemplate<IQuadrantReportSettings>>,
      where('active', '==', true)
    )
  );
};

/**
 * @description observes changes the subset of the quadrant settings that are not template specific
 * @param userId
 * @returns
 */
const quadrantSettingsOptionsProvider = (
  args: IListenerArgs
): Observable<IQuadrantReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/quadrantReport`
    ) as DocumentReference<IQuadrantReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IQuadrantReportSettingEntity
      ): IQuadrantReportSettingOptions => ({
        timeframe: entity?.timeframe,
        comparisonPopulation: entity?.comparisonPopulation,
      })
    ),
    distinctUntilChanged(
      (
        prev: IQuadrantReportSettingOptions,
        curr: IQuadrantReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: IListenerArgs
): Observable<ISettingsTemplate<IQuadrantReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/quadrantReport`
    ) as DocumentReference<IQuadrantReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IQuadrantReportSettingEntity
      ): ISettingsTemplate<IQuadrantReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IQuadrantReportSettings>,
        curr: ISettingsTemplate<IQuadrantReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};
